import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    IconButton,
    TextField,
    Button,
    Stack,
    Paper,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
} from '@mui/material';
import { EditRounded, Cancel, AddCircleOutline } from '@mui/icons-material';
import { Instagram, Youtube, Facebook, Twitter, Music } from 'lucide-react';
import { IWorkProfile, IProfileSocials, TPlatform } from '../../types/global/helper';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ThemeStyle } from '../../module/ThemeManager';
import ThspianIcon from '../CustomSVGIcons/ThspianIcon';
import CustomButton from '../Button/CustomButton';
import { formatSocialHandle, stripLeadingAt } from '../../utils/socialHandle';

export interface ISocialMediaSectionProps {
    workProfile?: IWorkProfile;
    themeStyle: ThemeStyle;
    isEditMode?: boolean;
    previewMode?: boolean;
    onSubmitForm?: (data: any) => void;
}

const PLATFORMS: { value: TPlatform; label: string; icon: any; color: string }[] = [
    { value: 'instagram', label: 'Instagram', icon: Instagram, color: '#E4405F' },
    { value: 'youtube', label: 'YouTube', icon: Youtube, color: '#FF0000' },
    { value: 'tiktok', label: 'TikTok', icon: Music, color: '#000000' },
    { value: 'facebook', label: 'Facebook', icon: Facebook, color: '#1877F2' },
    { value: 'twitter', label: 'Twitter', icon: Twitter, color: '#1DA1F2' },
    { value: 'thspian', label: 'Thspian', icon: ThspianIcon, color: '#E38627' },
];

const getPlatformIcon = (platform: TPlatform) => {
    const platformData = PLATFORMS.find(p => p.value === platform);
    return platformData?.icon || Instagram;
};

const getPlatformColor = (platform: TPlatform) => {
    const platformData = PLATFORMS.find(p => p.value === platform);
    return platformData?.color || '#E4405F';
};

const getPlatformLabel = (platform: TPlatform) => {
    const platformData = PLATFORMS.find(p => p.value === platform);
    return platformData?.label || platform;
};

const getSocialMediaUrl = (platform: TPlatform, username: string) => {
    const handle = stripLeadingAt(username);
    switch (platform) {
        case 'instagram':
            return `https://www.instagram.com/${handle}`;
        case 'youtube':
            return `https://www.youtube.com/@${handle}`;
        case 'tiktok':
            return `https://www.tiktok.com/@${handle}`;
        case 'facebook':
            return `https://www.facebook.com/${handle}`;
        case 'twitter':
            return `https://www.twitter.com/${handle}`;
        case 'thspian':
            return `https://thspian.com/${handle}`;
        default:
            return '';
    }
};

const formatNumber = (num?: number | null): string => {
    const value = Number(num);
    if (!Number.isFinite(value)) return '—';
    if (value >= 1000000) {
        return (value / 1000000).toFixed(1) + 'M';
    }
    if (value >= 1000) {
        return (value / 1000).toFixed(1) + 'K';
    }
    return value.toString();
};

const formatEngagementRate = (rate?: number | null): string => {
    const value = Number(rate);
    if (!Number.isFinite(value)) return '—';
    return `${value.toFixed(1)}%`;
};

export const SocialMediaSection: React.FC<ISocialMediaSectionProps> = ({
    workProfile,
    themeStyle,
    isEditMode = false,
    previewMode = false,
    onSubmitForm,
}) => {
    const [socials, setSocials] = useState<IProfileSocials[]>(workProfile?.socials || []);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newSocial, setNewSocial] = useState<IProfileSocials>({
        platform: 'instagram',
        bio: '',
        engagement: 0,
        followers: 0,
        following: 0,
        username: '',
    });

    useEffect(() => {
        setSocials(workProfile?.socials || []);
    }, [workProfile]);

    const handleSave = () => {
        if (onSubmitForm) {
            onSubmitForm({ socials });
        }
    };

    const handleAddSocial = () => {
        if (newSocial.bio && newSocial.followers > 0) {
            setSocials([...socials, newSocial]);
            setNewSocial({
                platform: 'instagram',
                bio: '',
                engagement: 0,
                followers: 0,
                following: 0,
                username: '',
            });
            setShowAddForm(false);
        }
    };

    const handleEditSocial = (index: number) => {
        setEditingIndex(index);
        setNewSocial(socials[index]);
    };

    const handleUpdateSocial = () => {
        if (editingIndex !== null && newSocial.bio && newSocial.followers > 0) {
            const updatedSocials = [...socials];
            updatedSocials[editingIndex] = newSocial;
            setSocials(updatedSocials);
            setEditingIndex(null);
            setNewSocial({
                platform: 'instagram',
                bio: '',
                engagement: 0,
                followers: 0,
                following: 0,
                username: '',
            });
        }
    };

    const handleDeleteSocial = (index: number) => {
        const updatedSocials = socials.filter((_, i) => i !== index);
        setSocials(updatedSocials);
    };

    const handleCancel = () => {
        setEditingIndex(null);
        setShowAddForm(false);
        setNewSocial({
            platform: 'instagram',
            bio: '',
            engagement: 0,
            followers: 0,
            following: 0,
            username: '',
        });
    };

    // Filter socials with values for display
    const socialsWithValues = socials.filter(social => social.bio && social.followers > 0);

    // Calculate aggregated metrics
    const totalReach = socialsWithValues.reduce((sum, social) => sum + (social.followers ?? 0), 0);
    const totalEngagement = socialsWithValues.reduce((sum, social) => sum + (social.engagement ?? 0), 0);
    const avgEngagementRate = socialsWithValues.length > 0 ? totalEngagement / socialsWithValues.length : 0;
    const isContentCreatorLayout = workProfile?.profile_type === 'content_creator';

    return (
        <Box sx={{ mb: isContentCreatorLayout ? 0 : 3, bgcolor: 'transparent', boxShadow: 'none' }}>
            {/* Section Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 0,
                }}>
                <Typography
                    sx={{
                        color: theme[themeStyle].tag_white,
                        fontFamily: FONT_ENUM.BOLD_HEADER,
                        fontSize: isContentCreatorLayout ? '1rem' : '18px',
                        fontWeight: isContentCreatorLayout ? 800 : 700,
                    }}>
                    Social Media
                </Typography>
                {!previewMode && isEditMode && (
                    <Button
                        onClick={() => setShowAddForm(true)}
                        startIcon={<AddCircleOutline sx={{ fontSize: 16 }} />}
                        sx={{
                            px: 2.25,
                            py: 1.2,
                            borderRadius: '16px',
                            bgcolor: isContentCreatorLayout ? 'rgba(250,175,63,0.12)' : 'transparent',
                            color: theme[themeStyle].highlight,
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 12,
                            '&:hover': {
                                bgcolor: 'rgba(250,175,63,0.2)',
                            },
                        }}>
                        Add Social
                    </Button>
                )}
            </Box>

            {/* Aggregated Metrics */}
            {!isContentCreatorLayout &&
                ((isEditMode && socials.length > 0) || (!isEditMode && socialsWithValues.length > 0)) && (
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            mb: 2,
                            backgroundColor: theme[themeStyle].form_black,
                            borderRadius: '8px',
                            border: `1px solid ${theme[themeStyle].tag_white}`,
                        }}>
                        <Typography
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: '16px',
                                mb: 1,
                            }}>
                            Total Reach & Engagement
                        </Typography>
                        <Stack direction="row" spacing={3}>
                            <Box>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].highlight,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: '20px',
                                    }}>
                                    {formatNumber(totalReach)}
                                </Typography>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].light_gray_text,
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        fontSize: '12px',
                                    }}>
                                    Total Followers
                                </Typography>
                            </Box>
                            <Box>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].highlight,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: '20px',
                                    }}>
                                    {formatEngagementRate(avgEngagementRate)}
                                </Typography>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].light_gray_text,
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        fontSize: '12px',
                                    }}>
                                    Avg Engagement
                                </Typography>
                            </Box>
                        </Stack>
                    </Paper>
                )}

            {/* Add/Edit Form */}
            {(showAddForm || editingIndex !== null) && (
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        mb: 2,
                        backgroundColor: theme[themeStyle].form_black,
                        borderRadius: '8px',
                        border: `1px solid ${theme[themeStyle].tag_white}`,
                    }}>
                    <Typography
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: '16px',
                            mb: 2,
                        }}>
                        {editingIndex !== null ? 'Edit Social Media' : 'Add Social Media'}
                    </Typography>
                    <Stack spacing={2}>
                        <FormControl fullWidth>
                            <InputLabel
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                }}>
                                Platform
                            </InputLabel>
                            <Select
                                value={newSocial.platform}
                                onChange={e => setNewSocial({ ...newSocial, platform: e.target.value as TPlatform })}
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].tag_white,
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                }}>
                                {PLATFORMS.map(platform => (
                                    <MenuItem key={platform.value} value={platform.value}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <platform.icon size={16} style={{ color: platform.color }} />
                                            {platform.label}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Username"
                            value={newSocial.username}
                            onChange={e => setNewSocial({ ...newSocial, bio: e.target.value })}
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: theme[themeStyle].tag_white,
                                    '& fieldset': {
                                        borderColor: theme[themeStyle].tag_white,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    color: theme[themeStyle].tag_white,
                                },
                            }}
                        />
                        <TextField
                            label="Followers"
                            type="number"
                            value={newSocial.followers}
                            onChange={e => setNewSocial({ ...newSocial, followers: parseInt(e.target.value) || 0 })}
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: theme[themeStyle].tag_white,
                                    '& fieldset': {
                                        borderColor: theme[themeStyle].tag_white,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    color: theme[themeStyle].tag_white,
                                },
                            }}
                        />
                        <TextField
                            label="Engagement Rate (%)"
                            type="number"
                            value={newSocial.engagement}
                            onChange={e => setNewSocial({ ...newSocial, engagement: parseFloat(e.target.value) || 0 })}
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: theme[themeStyle].tag_white,
                                    '& fieldset': {
                                        borderColor: theme[themeStyle].tag_white,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    color: theme[themeStyle].tag_white,
                                },
                            }}
                        />
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="contained"
                                onClick={editingIndex !== null ? handleUpdateSocial : handleAddSocial}
                                sx={{
                                    backgroundColor: theme[themeStyle].highlight,
                                    color: theme[themeStyle].tag_white,
                                    '&:hover': {
                                        backgroundColor: theme[themeStyle].highlight,
                                    },
                                }}>
                                {editingIndex !== null ? 'Update' : 'Add'}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={handleCancel}
                                sx={{
                                    borderColor: theme[themeStyle].tag_white,
                                    color: theme[themeStyle].tag_white,
                                    '&:hover': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                }}>
                                Cancel
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>
            )}

            {/* Social Media List */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: isContentCreatorLayout
                        ? { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }
                        : '1fr',
                    gap: 2,
                    mt: isContentCreatorLayout ? 3.5 : 2,
                }}>
                {(isEditMode ? socials : socialsWithValues).map((social, index) => (
                    <Paper
                        key={index}
                        elevation={0}
                        sx={{
                            ...(isContentCreatorLayout ? { px: 2.5, py: 2 } : { p: 2 }),
                            backgroundColor: '#20201f',
                            borderRadius: isContentCreatorLayout ? '22px' : '8px',
                            border: '1px solid rgba(255,255,255,0.04)',
                            display: isContentCreatorLayout ? 'flex' : 'block',
                            flexDirection: isContentCreatorLayout ? 'column' : undefined,
                            minHeight: isContentCreatorLayout ? (isEditMode ? 172 : 158) : undefined,
                        }}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                mb: isContentCreatorLayout ? 0 : 2,
                                flexShrink: 0,
                            }}>
                            <Box
                                onClick={() =>
                                    window.open(getSocialMediaUrl(social.platform, social.username), '_blank')
                                }
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.25,
                                    cursor: 'pointer',
                                    minWidth: 0,
                                }}>
                                <Box
                                    sx={{
                                        width: 52,
                                        height: 52,
                                        borderRadius: '16px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: social.platform === 'instagram' ? '#E4405F' : '#0b0b0b',
                                        color:
                                            social.platform === 'instagram' ? '#ffffff' : theme[themeStyle].tag_white,
                                        flexShrink: 0,
                                    }}>
                                    {React.createElement(getPlatformIcon(social.platform), {
                                        size: 22,
                                        style: {
                                            color:
                                                social.platform === 'instagram'
                                                    ? '#ffffff'
                                                    : social.platform === 'thspian'
                                                    ? '#ffffff'
                                                    : getPlatformColor(social.platform),
                                        },
                                    })}
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.BOLD_HEADER,
                                            fontSize: isContentCreatorLayout ? '1rem' : '16px',
                                            lineHeight: 1.05,
                                            fontWeight: 700,
                                        }}>
                                        {getPlatformLabel(social.platform)}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].light_gray_text,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            fontSize: 12,
                                            mt: 0.4,
                                        }}>
                                        {formatSocialHandle(social.username)}
                                    </Typography>
                                </Box>
                            </Box>
                            {isEditMode && !previewMode && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, ml: 1, flexShrink: 0 }}>
                                    <IconButton
                                        onClick={e => {
                                            e.stopPropagation();
                                            handleEditSocial(index);
                                        }}
                                        size="small"
                                        sx={{ color: theme[themeStyle].highlight, p: 0.35 }}>
                                        <EditRounded sx={{ fontSize: 18 }} />
                                    </IconButton>
                                    <IconButton
                                        onClick={e => {
                                            e.stopPropagation();
                                            handleDeleteSocial(index);
                                        }}
                                        size="small"
                                        sx={{ color: theme[themeStyle].secondary_red, p: 0.35 }}>
                                        <Cancel sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Box>
                            )}
                        </Box>
                        <Stack
                            direction="row"
                            spacing={4}
                            sx={{
                                mt: isContentCreatorLayout ? 'auto' : 0,
                                pb: isContentCreatorLayout ? 1.25 : 0,
                                justifyContent: 'space-between',
                                alignItems: 'flex-end',
                            }}>
                            <Box>
                                <Typography
                                    sx={{
                                        color: 'rgba(255,255,255,0.4)',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: '0.75rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.08em',
                                    }}>
                                    Followers
                                </Typography>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].primary_white,
                                        fontFamily: FONT_ENUM.BOLD_HEADER,
                                        fontSize: isContentCreatorLayout ? '1.55rem' : '18px',
                                        mt: 0.5,
                                        lineHeight: 1,
                                    }}>
                                    {formatNumber(social.followers)}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography
                                    sx={{
                                        color: 'rgba(255,255,255,0.4)',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: '0.75rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.08em',
                                    }}>
                                    Engagement
                                </Typography>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].highlight,
                                        fontFamily: FONT_ENUM.BOLD_HEADER,
                                        fontSize: isContentCreatorLayout ? '1.55rem' : '18px',
                                        mt: 0.5,
                                        lineHeight: 1,
                                    }}>
                                    {formatEngagementRate(social.engagement)}
                                </Typography>
                            </Box>
                        </Stack>
                    </Paper>
                ))}
            </Box>

            {/* Save Button */}
            {isEditMode && socials.length > 0 && (
                <Box sx={{ mt: 2.5, bgcolor: 'transparent' }}>
                    <CustomButton
                        label="Save Social Media"
                        onClick={handleSave}
                        customStyle={
                            isContentCreatorLayout
                                ? {
                                      borderRadius: '18px',
                                      height: '54px',
                                  }
                                : undefined
                        }
                        sx={
                            isContentCreatorLayout
                                ? {
                                      width: '100%',
                                      backgroundColor: theme[themeStyle].highlight,
                                      color: theme[themeStyle].black,
                                      '&:hover': {
                                          backgroundColor: theme[themeStyle].primary_orange,
                                          WebkitTextFillColor: theme[themeStyle].black,
                                      },
                                  }
                                : {
                                      '&:hover': {
                                          WebkitTextFillColor: theme[themeStyle].primary_orange,
                                      },
                                  }
                        }
                    />
                </Box>
            )}
        </Box>
    );
};
